import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import { isMobile, wait } from 'helpers/utils';
import isEqual from 'lodash/isEqual';
import GaEventManager from 'services/ga/gaEventManager';
import { Actions, Labels } from 'services/ga/interfaces';
import { filterMyAuctions } from 'state/auctions.slice/filters';
import { auctionsWatchActions } from 'state/auctionsWatch.slice';
import { getFilterTextByValues, getQuickFiltersInitialState } from 'state/helpers';
import { locationChange } from 'state/router.actions';
import { countersActions } from './../counters.slice/counters.slice';
import { typeToQuickFilters } from './constants';

const fetchMyAuctions = createAsyncThunk(
  'myAuctions/fetchMyAuctions',
  async ({ type }: { type: MyAuctionsTypes.MyAuctionsTypes }, { getState, dispatch, signal }) => {
    const {
      myAuctions: { pager, rawAuctionsList },
      counters: { myAuctions },
      auctionsWatch: { pending },
    } = getState() as RootState;

    if (pending && pending.show) {
      dispatch(
        auctionsWatchActions.setAuctionAsWatching({
          auction: { auction_id: pending?.auctionId, watch: pending?.watch },
        }),
      );
      dispatch(auctionsWatchActions.clearPending());
    }

    const { data: respData }: AxiosResponse<MyAuctionsTypes.MyAuctionsApiData<typeof type>> =
      await axios.post(
        `/api/buyers/my_auctions`,
        {
          type,
          page: pager.page,
          per_page: pager.perPage,
        },
        { signal },
      );

    const { auctionsWatch } = getState() as RootState;

    const counts = {
      following: Object.keys(auctionsWatch.auctions).length,
      didnt_win: respData.totals.didnt_win,
      in_prog: respData.totals.in_prog,
      won: respData.totals.won,
    };

    const didCountersChange =
      myAuctions.won !== counts.won ||
      myAuctions.in_prog !== counts.in_prog ||
      myAuctions.didnt_win !== counts.didnt_win;

    if (didCountersChange) {
      dispatch(countersActions.setCount({ type: 'myAuctions', counts }));
    }

    const auctions = [...rawAuctionsList, ...respData[type]];

    const hasMore =
      respData[type].length === pager.perPage && auctions.length < respData.totals[type];

    return {
      auctionsList: auctions,
      rawAuctionsList: auctions,
      pager: {
        ...pager,
        hasMore,
      },
    };
  },
);

const fetchMyAuctionsInProgress = createAsyncThunk(
  'myAuctions/fetchMyAuctionsInProgress',
  async (_, { getState, dispatch, signal }) => {
    const {
      auctionsWatch: { pending },
    } = getState() as RootState;

    if (pending && pending.show) {
      dispatch(
        auctionsWatchActions.setAuctionAsWatching({
          auction: { auction_id: pending?.auctionId, watch: pending?.watch },
        }),
      );
      dispatch(auctionsWatchActions.clearPending());
    }

    const { data }: AxiosResponse<{ auctions: MyAuctionsTypes.MyAuctionType[] }> = await axios.get(
      `/api/buyers/auction_bids_and_end_time?my_auctions=true`,
      { signal },
    );

    const {
      counters: { myAuctions },
    } = getState() as RootState;

    const inProgressAuctions = data.auctions
      .reduce((res: MyAuctionsTypes.MyAuctionType[], auction) => {
        if (auction.your_bid && auction.end_time && new Date(auction.end_time) > new Date()) {
          return [
            ...res,
            {
              ...auction,
              active: true,
              next_bid: auction.next_bid && Math.ceil(auction.next_bid),
            },
          ];
        }

        return res;
      }, [])
      .sort(
        (a, b) =>
          (a.end_time ? new Date(a.end_time).valueOf() : 0) -
          (b.end_time ? new Date(b.end_time).valueOf() : 0),
      );

    const state = getState() as RootState;
    const { isFiltered, count, auctions } = filterMyAuctions('in_prog', state, inProgressAuctions);

    if (myAuctions.in_prog !== count && !isFiltered) {
      dispatch(countersActions.updateCount({ type: 'in_prog', count }));
    }
    dispatch(countersActions.updateFilteredCount({ type: 'in_prog', count }));

    return {
      auctionsList: auctions,
      rawAuctionsList: auctions,
      isFiltered,
      type: 'in_prog',
    };
  },
);

const fetchMyAuctionsWatch = createAsyncThunk(
  'myAuctions/fetchMyAuctionsWatch',
  async (_, { getState, dispatch, signal }) => {
    const {
      auctionsWatch: { pending },
    } = getState() as RootState;

    if (pending && pending.show) {
      dispatch(
        auctionsWatchActions.setAuctionAsWatching({
          auction: { auction_id: pending?.auctionId, watch: pending?.watch },
        }),
      );
      dispatch(auctionsWatchActions.clearPending());
    }

    const { data }: AxiosResponse<{ auctions: MyAuctionsTypes.MyAuctionType[] }> = await axios.get(
      `/api/buyers/auction_bids_and_end_time?my_auctions=true`,
      { signal },
    );

    const {
      counters: { myAuctions },
      auctionsWatch,
    } = getState() as RootState;

    const watchAuctionsList = data.auctions
      .reduce((res: MyAuctionsTypes.MyAuctionType[], auction) => {
        if (
          auctionsWatch.auctions[auction.auction_id] &&
          auction.end_time &&
          new Date(auction.end_time) > new Date()
        ) {
          return [
            ...res,
            {
              ...auction,
              active: true,
              next_bid: auction.next_bid && Math.ceil(auction.next_bid),
            },
          ];
        }

        return res;
      }, [])
      .sort(
        (a, b) =>
          (a.end_time ? new Date(a.end_time).valueOf() : 0) -
          (b.end_time ? new Date(b.end_time).valueOf() : 0),
      );

    const count = Object.keys(auctionsWatch.auctions).length;

    if (myAuctions.following !== count) {
      dispatch(
        countersActions.updateCount({
          type: 'following',
          count,
        }),
      );
    }

    return {
      auctionsList: watchAuctionsList,
      rawAuctionsList: watchAuctionsList,
    };
  },
);

const filterMyAuctionsList = createAsyncThunk(
  'myAuctions/filterMyAuctionsList',
  async ({ type }: { type?: MyAuctionsTypes.MyAuctionsTypes }, { getState, dispatch }) => {
    if (!type) {
      return;
    }
    const state = getState() as RootState;
    const { quickFilters, rawAuctionsList } = state.myAuctions;
    if (!quickFilters) {
      return;
    }

    const { isFiltered, count, auctions } = filterMyAuctions(type, state, rawAuctionsList);

    if (type !== 'all') {
      if (!isFiltered) {
        dispatch(countersActions.updateCount({ type, count }));
      }
      dispatch(countersActions.updateFilteredCount({ type, count }));
    }

    return {
      auctionsList: auctions,
      isFiltered,
      type,
    };
  },
);

export const initialState: MyAuctionsTypes.MyAuctionsState = {
  auctionsList: [],
  rawAuctionsList: [],
  pager: {
    page: 1,
    perPage: isMobile() ? 10 : 20,
    hasMore: false,
  },
  isLoading: true,
  isFiltered: false,
  quickFilters: {
    all: [],
    in_prog: [],
    won: [],
    didnt_win: [],
    following: [],
  },
  filteredAuctions: {},
};

export const myAuctionsSlice = createSlice({
  name: 'myAuctions',
  initialState,
  reducers: {
    setFiltersInitialData: (state) => {
      const quickFilters = getQuickFiltersInitialState(typeToQuickFilters);
      return {
        ...state,
        quickFilters,
      };
    },
    onFilterChange: (
      state,
      action: PayloadAction<{
        type: MyAuctionsTypes.MyAuctionsTypes;
        index: number;
        value: any;
        quickFilters?: boolean;
      }>,
    ) => {
      const { type, index, value, quickFilters } = action.payload;

      if (!quickFilters || !state.quickFilters) {
        return state;
      }

      const changedFilters = state.quickFilters;
      const nextStateForType = [...changedFilters[type]];
      const changedField = { ...nextStateForType[index] };
      let newValue = value;

      changedField.value = newValue;
      nextStateForType[index] = {
        ...nextStateForType[index],
        value: newValue,
        isChanged: !isEqual(newValue, nextStateForType[index].initialValue),
      };

      const filterValue = getFilterTextByValues(changedField);
      if (filterValue && filterValue.length > 0) {
        GaEventManager.GA4InputChange(changedField.type, changedField.name, {
          location4: 'filtersPanel',
          actionedObject: changedField.name,
          actionedObject2: filterValue,
        });
      }

      return {
        ...state,
        quickFilters: {
          ...state.quickFilters,
          [type]: nextStateForType,
        },
      };
    },
    onFiltersReset: (state, action: PayloadAction<{ type: MyAuctionsTypes.MyAuctionsTypes }>) => {
      const {
        payload: { type },
      } = action;

      state.quickFilters[type] = getQuickFiltersInitialState(typeToQuickFilters)[type];
    },
    updateBibData: (
      state,
      action: PayloadAction<{ bidsData: Partial<MyAuctionsTypes.MyAuctionType> }>,
    ) => {
      const { payload } = action;

      const auctions = state.rawAuctionsList.map((auction: MyAuctionsTypes.MyAuctionType) =>
        auction.auction_id !== payload.bidsData.auction_id
          ? auction
          : { ...auction, ...action.payload.bidsData, active: true },
      );

      return {
        ...state,
        rawAuctionsList: auctions,
      };
    },
    incrementPage: (state) => {
      state.pager.page += 1;
    },
    resetPage: (state) => {
      return {
        ...state,
        pager: {
          ...state.pager,
          page: 1,
          hasMore: false,
        },
      };
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pager.page = action.payload;
    },
    removeAuction: (state, action: PayloadAction<{ auction: { auction_id: number } }>) => {
      if (state.auctionsList && action.payload.auction?.auction_id) {
        state.auctionsList = state.auctionsList.filter(
          (item: MyAuctionsTypes.MyAuctionType) =>
            item.auction_id !== action.payload.auction.auction_id,
        );
      }
    },
    appendAuction: (
      state,
      action: PayloadAction<{ auction: MyAuctionsTypes.MyAuctionType; index?: number }>,
    ) => {
      const auctionsList = [...state.auctionsList];
      auctionsList.splice(action.payload.index || 0, 0, { ...action.payload.auction, watch: true });

      return {
        ...state,
        auctionsList,
      };
    },
    resetData: (state) => {
      return {
        ...state,
        data: undefined,
        rawData: undefined,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMyAuctions.pending, (state) => {
      return {
        ...state,
        isLoading: true,
        auctionsList: state.pager.page === 1 ? [] : state.auctionsList,
        rawAuctionsList: state.pager.page === 1 ? [] : state.rawAuctionsList,
      };
    });
    builder.addCase(fetchMyAuctions.fulfilled, (state, action) => {
      const { payload } = action;

      return {
        ...state,
        isLoading: false,
        ...payload,
      };
    });
    builder.addCase(fetchMyAuctions.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.isLoading = false;
      }
    });
    builder.addCase(fetchMyAuctionsInProgress.pending, (state) => {
      return {
        ...state,
        isLoading: true,
        auctionsList: [],
        rawAuctionsList: [],
      };
    });
    builder.addCase(fetchMyAuctionsInProgress.fulfilled, (state, action) => {
      const { payload } = action;

      return {
        ...state,
        isLoading: false,
        ...payload,
        filteredAuctions: {
          ...state.filteredAuctions,
          [payload.type]: payload.isFiltered,
        },
      };
    });
    builder.addCase(fetchMyAuctionsInProgress.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.isLoading = false;
      }
    });
    builder.addCase(fetchMyAuctionsWatch.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchMyAuctionsWatch.fulfilled, (state, action) => {
      const { payload } = action;

      return {
        ...state,
        isLoading: false,
        ...payload,
      };
    });
    builder.addCase(fetchMyAuctionsWatch.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.isLoading = false;
      }
    });
    builder.addCase(filterMyAuctionsList.fulfilled, (state, action) => {
      const { payload } = action;
      if (!payload) {
        return state;
      }

      return {
        ...state,
        ...payload,
        filteredAuctions: {
          ...state.filteredAuctions,
          [payload.type]: payload.isFiltered,
        },
      };
    });
    // actions triggered by other reducers
    builder.addCase(locationChange, (state, action) => {
      if (
        action.payload.location.pathname.includes('/auctions') &&
        action.payload.action === 'PUSH'
      ) {
        return {
          ...state,
          isLoading: true,
          auctionsList: [],
          rawAuctionsList: [],
        };
      }
      return state;
    });
  },
});

export const myAuctionsActions = {
  ...myAuctionsSlice.actions,
  fetchMyAuctions,
  fetchMyAuctionsInProgress,
  fetchMyAuctionsWatch,
  filterMyAuctionsList,
};

export default myAuctionsSlice.reducer;
